import { StatusProxy } from "@focus/focusUI";
import { useCallback } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { ContactsByOperatorButton } from "../components/ContactsByOperatorButton";
import { Table } from "../components/table/Table";
import { TABLE_ACTION } from "../components/table/TableActionColumn";
import { TableShell } from "../components/table/TableShell";
import {
  orbitConjunctionsColumnsLayout,
  useAvailableconjunctionHeaders,
  useSavedOrbitConjunctionColumns,
} from "../services/configuration";
import { CONJ_DEFAULT_VISIBLE_COLUMNS } from "../services/conjunctions/Headers";
import { downloadFileZip } from "../services/downloaduploadOrbits/actions";
import {
  getColumnDefinitions,
  type ServerColumnConfig,
} from "../services/helpers/headers";
import { useConfig } from "../services/helpers/hooks";
import { orbitConjunctions, useOrbitConjunctions } from "../services/orbits";
import type { RootState } from "../store";
import { RiskLevel } from "../types/conjunctions";
import { combineErrors, combineStatuses } from "../utils";
import type { ColumnFiltersState } from "../services/helpers/pager";

export const OrbitConjunctionsContainer = () => {
  const dispatch = useDispatch();
  const orbitConjunctionActions = orbitConjunctions.actions;
  const orbitConjunctionsLayoutActions = orbitConjunctionsColumnsLayout.actions;

  // Request the user active columns to the server's DB
  const headersConfig = useAvailableconjunctionHeaders();
  const savedColumnsLayout = useSavedOrbitConjunctionColumns();
  const columnDefinitions = getColumnDefinitions(headersConfig.value);

  const config = useConfig("orbit-conjunction");
  const conjunctions = useOrbitConjunctions(config);

  const saveColumnLayout = (serverConfig: ServerColumnConfig) => {
    dispatch(orbitConjunctionsLayoutActions.saveItems(serverConfig));
  };

  const orbitsFilter = useSelector(
    (state: RootState) => state.orbitConjunctions.filter,
    shallowEqual,
  );

  const handleFilterChange = useCallback(
    (filtered: ColumnFiltersState) =>
      dispatch(orbitConjunctionActions.setFilter(filtered)),
    [orbitConjunctionActions],
  );

  const combinedStatus = combineStatuses([
    headersConfig.status,
    savedColumnsLayout.status,
    conjunctions.status,
  ]);
  const combinedError = combineErrors([
    headersConfig.error,
    savedColumnsLayout.error,
    conjunctions.error,
  ]);

  return (
    <StatusProxy status={combinedStatus} error={combinedError}>
      <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-slate-50/40 p-5">
        <TableShell className="orbit-conjunctions-table-shell">
          <Table
            columnDefinitions={columnDefinitions}
            serverDisplayConfig={savedColumnsLayout.items}
            defaultVisibleValues={CONJ_DEFAULT_VISIBLE_COLUMNS}
            rowData={conjunctions.items}
            base={"conjunctions"}
            height="auto"
            actions={[
              TABLE_ACTION.DOWNLOAD,
              TABLE_ACTION.INFO,
              TABLE_ACTION.EVENT,
              TABLE_ACTION.PLOTS,
              (data) => <ContactsByOperatorButton data={data} />,
            ]}
            downloadData={(payload) => dispatch(downloadFileZip(payload))}
            rowClassRules={{
              "!bg-red-50": (params) =>
                params.original.riskLevel.value === RiskLevel.ALERT,
              "!bg-amber-50": (params) =>
                params.original.riskLevel.value === RiskLevel.WARNING,
            }}
            saveColumnLayout={saveColumnLayout}
            controlledColumnFilter={orbitsFilter}
            controlledColumnFilterChange={handleFilterChange}
          />
        </TableShell>
      </div>
    </StatusProxy>
  );
};
